import React, { useMemo, useState, useEffect } from "react";
import ReusableTable from "../components/Table/ReusableTable";
import {
  Users, CheckCircle2, AlertCircle, Clock, Search,
  ArrowRight, Filter, Download, Eye, Check, XCircle,
  Zap, Briefcase, FileText, ClipboardList, ShieldCheck,
  TrendingUp, Loader2
} from "lucide-react";
import Button from "../components/Button/Button";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { post } from "../utils/api";
import CustomDialog from "../components/dialog/CustomDialog";

const KpiCard = ({ title, value, icon: Icon, trend, color = "indigo" }) => (
  <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-all group animate-in fade-in slide-in-from-bottom-3 duration-500">
    <div className="flex justify-between items-start mb-4">
      <div className={`p-3 rounded-xl bg-${color}-50 text-${color}-500 group-hover:scale-110 transition-transform`}>
        <Icon size={20} />
      </div>
      {trend && (
        <span className="flex items-center gap-1 text-[10px] font-bold text-emerald-500 bg-emerald-50 px-2 py-0.5 rounded-full">
          <TrendingUp size={10} className="mr-0.5" /> {trend}
        </span>
      )}
    </div>
    <div>
      <p className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] mb-1">{title}</p>
      <h3 className="text-2xl font-black text-gray-800 tracking-tight">{value}</h3>
    </div>
  </div>
);

const AMCListing = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");
  const [selectedSeqNum, setSelectedSeqNum] = useState(null);
  const [globalSearch, setGlobalSearch] = useState("");

  const [isApproveModalOpen, setIsApproveModalOpen] = useState(false);
  const [selectedDistributorForApproval, setSelectedDistributorForApproval] =
    useState(null);
  const [viewedAgreements, setViewedAgreements] = useState({});

  // E-sign state
  const [isEsignModalOpen, setIsEsignModalOpen] = useState(false);
  const [esignName, setEsignName] = useState("");
  const [esignLoading, setEsignLoading] = useState(false);
  const [selectedDistributorForEsign, setSelectedDistributorForEsign] =
    useState(null);

  const fetchData = async () => {
    try {
      setLoading(true);
      // Fetch all AMC listings without email filter
      const response = await post("/onboarding/fetchAMCListing", {});

      if (response.data?.Success) {
        const mappedData = response.data.Data.map((item) => {
          const uniqueFunds = item.products
            ? [
              ...new Set(
                item.products.map((p) => p.fund_name).filter(Boolean)
              ),
            ]
            : [];
          const uniqueClasses = item.products
            ? [
              ...new Set(
                item.products.map((p) => p.class_code).filter(Boolean)
              ),
            ]
            : [];

          return {
            distributor_name: item.distributor_name,
            arn_code: item.arn_code,
            pan: item.pan_number,
            distributor_sequence_number: item.distributor_sequence_number,
            distributor_status: item.distributor_status,
            agreement_status: item.agreement_status,
            fundName: uniqueFunds.join(", ") || "-",
            class: uniqueClasses.join(", ") || "-",
            products: item.products || [],
            id: item.id, // Ensure ID is available if needed for esign API
          };
        });
        setData(mappedData);
      } else {
        if (response.data?.Message !== "No Records Found") {
          toast.error(response.data?.Message || "Failed to fetch data");
        }
        setData([]);
      }
    } catch (error) {
      console.error("Fetch error:", error);
      toast.error("Error fetching distributor listing");
    } finally {
      setLoading(false);
    }
  };

  const stats = useMemo(() => ({
    total: data.length,
    pending: data.filter(d => (d.agreement_status || "").toLowerCase().includes("pending")).length,
    executed: data.filter(d => (d.agreement_status || "").toLowerCase().includes("executed") || (d.agreement_status || "").toLowerCase().includes("approved")).length
  }), [data]);

  useEffect(() => {
    fetchData();
  }, []);

  const handleApprove = (row) => {
    setSelectedDistributorForApproval(row);
    setIsApproveModalOpen(true);
  };

  const confirmApprove = async () => {
    if (!selectedDistributorForApproval) return;

    try {
      const response = await post("/onboarding/ApproveRejectAMC", {
        distributor_sequence_number:
          selectedDistributorForApproval.distributor_sequence_number,
        status: "Approved",
        reason: null,
      });

      if (response.data?.Success) {
        toast.success("Distributor approved successfully");
        setIsApproveModalOpen(false);
        fetchData();
      } else {
        toast.error(response.data?.Message || "Approval failed");
      }
    } catch (error) {
      console.error("Approval error:", error);
      toast.error("Error approving distributor");
    }
  };

  const handleRejectInit = (row) => {
    setSelectedSeqNum(row.distributor_sequence_number);
    setIsRejectModalOpen(true);
    setRejectionReason("");
  };

  const handleRejectSubmit = async () => {
    if (!selectedSeqNum) return;
    if (!rejectionReason.trim()) {
      toast.warn("Please enter a rejection reason");
      return;
    }

    try {
      const response = await post("/onboarding/ApproveRejectAMC", {
        distributor_sequence_number: selectedSeqNum,
        status: "Rejected",
        reason: rejectionReason,
      });

      if (response.data?.Success) {
        toast.success("Distributor rejected successfully");
        setIsRejectModalOpen(false);
        fetchData();
      } else {
        toast.error(response.data?.Message || "Rejection failed");
      }
    } catch (error) {
      console.error("Rejection error:", error);
      toast.error("Error rejecting distributor");
    }
  };

  const handleDownload = async (row) => {
    try {
      if (!row.distributor_sequence_number) {
        toast.error("Distributor Sequence Number missing for this entry.");
        return;
      }

      toast.info("Generating and downloading agreement...");

      const response = await post("/fileGeneration/downloadGeneratedAgreement", {
        distributor_sequence_number: row.distributor_sequence_number,
      });

      if (response.data && response.data.Success && response.data.Data) {
        setViewedAgreements((prev) => ({
          ...prev,
          [row.distributor_sequence_number]: true,
        }));

        const byteCharacters = atob(response.data.Data);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: "application/pdf" });

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          `Agreement_${row.distributor_name || "Document"}.pdf`
        );
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);
        window.URL.revokeObjectURL(url);

        toast.success("Download started successfully");
      } else {
        console.error("Download response error:", response.data);
        toast.error(response.data?.Message || "Failed to generate agreement");
      }
    } catch (error) {
      console.error("Download failed:", error);
      toast.error(
        error.response?.data?.Message || "Failed to download agreement"
      );
    }
  };

  const handleEsignInit = (row) => {
    const hasViewedAgreement = Boolean(
      viewedAgreements[row.distributor_sequence_number]
    );
    if (!hasViewedAgreement) {
      toast.warning("Please view agreement first before initiating E-sign.");
      return;
    }
    setSelectedDistributorForEsign(row);
    setEsignName("");
    setIsEsignModalOpen(true);
  };
  const handleEnableLocationAccess = () => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        toast.error("Geolocation is not supported by your browser.");
        reject(new Error("Geolocation not supported"));
        return;
      }

      toast.info("Requesting browser location permission for E-signing...");

      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve(position);
        },
        (error) => {
          console.warn("Location access error:", error);
          toast.error("Location access is required for E-signing. Please allow location in browser site settings and try again.");
          reject(error);
        },
        { timeout: 10000 }
      );
    });
  };

  const handleOpsEsignSubmit = async () => {
    if (
      !selectedDistributorForEsign?.id &&
      !selectedDistributorForEsign?.distributor_sequence_number
    ) {
      toast.error("Missing distributor id");
      return;
    }

    if (!esignName.trim()) {
      toast.warn("Please enter the name for E-sign");
      return;
    }

    try {
      setEsignLoading(true);

      // Request browser location permission first
      try {
        await handleEnableLocationAccess();
      } catch (locErr) {
        setEsignLoading(false);
        return;
      }

      // AMC e-sign initiation API
      const payload = {
        name_2: esignName.trim(), // signer name for AMC
        distributor_id:
          selectedDistributorForEsign.id ||
          selectedDistributorForEsign.distributor_sequence_number,
      };

      const signzyResp = await post(
        "/eSign/initiate-signzy-contract-amc",
        payload
      );

      if (!signzyResp.data?.Success) {
        toast.error(
          signzyResp.data?.Message || "Failed to initiate AMC Signzy contract"
        );
        setEsignLoading(false);
        return;
      }

      const data = signzyResp.data.Data;

      // ⭐⭐ STORE THE NEW CONTRACT ID FROM AMC API RESPONSE
      const amcContractId = data?.contractId;
      console.log("AMC Contract ID:", amcContractId);

      if (amcContractId) {
        localStorage.setItem("ops_contractId", amcContractId);
        console.log("Stored ops_contractId in localStorage:", amcContractId);
      }

      // Extract signer URL from AMC API response
      const signerUrl = data?.signerdetail?.[0]?.workflowUrl;

      if (!signerUrl) {
        toast.error("Signer URL missing from Signzy AMC response!");
        setEsignLoading(false);
        return;
      }

      // Open Signzy workflow in new tab
      const win = window.open(signerUrl, "_blank");
      if (!win) {
        toast.error("Pop-up blocked! Please allow pop-ups for this site to open E-sign window.");
      }

      // Mark AMC E-sign as complete
      const approveResp = await post("/onboarding/amc/approve", {
        id:
          selectedDistributorForEsign.id ||
          selectedDistributorForEsign.distributor_sequence_number,
        esign_name: esignName.trim(),
      });

      if (approveResp.data?.Success) {
        toast.success(
          approveResp.data.Message || "Approved by AMC with E-sign"
        );
      } else {
        toast.error(approveResp.data?.Message || "AMC approval failed");
      }

      setIsEsignModalOpen(false);
      setEsignName("");
      setSelectedDistributorForEsign(null);
      await fetchData();
    } catch (err) {
      console.error("AMC E-sign error:", err);
      toast.error("AMC E-sign failed. Check console.");
    } finally {
      setEsignLoading(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        Header: "Distributor Name",
        accessor: "distributor_name",
        size: 200,
        Cell: ({ value }) => (
          <div className="font-medium text-gray-900">{value || "-"}</div>
        ),
      },
      {
        Header: "APRN Code",
        accessor: "arn_code",
        size: 150,
      },
      {
        Header: "PAN Number",
        accessor: "pan",
        size: 150,
      },
      {
        Header: "Fund Name",
        accessor: "fundName",
        size: 250,
        Cell: ({ value }) => (
          <div className="truncate" title={value}>
            {value}
          </div>
        ),
      },
      {
        Header: "Class",
        accessor: "class",
        size: 150,
        Cell: ({ value }) => (
          <div className="truncate" title={value}>
            {value}
          </div>
        ),
      },
      {
        Header: "Status",
        accessor: "agreement_status",
        size: 180,
        Cell: ({ value }) => {
          let styles = "bg-gray-50 text-gray-500 border-gray-100";
          const status = (value || "Pending").toLowerCase();

          if (status.includes("approved") || status.includes("executed")) styles = "bg-emerald-50 text-emerald-700 border-emerald-100";
          else if (status.includes("rejected")) styles = "bg-rose-50 text-rose-700 border-rose-100";
          else if (status.includes("pending")) styles = "bg-amber-50 text-amber-700 border-amber-100";

          return (
            <div className={`px-4 py-1.5 rounded-full text-xs font-semibold border inline-flex items-center ${styles}`}>
              {value || "Pending"}
            </div>
          );
        },
      },
      {
        Header: "ACTION",
        accessor: "action",
        size: 300,
        Cell: ({ row }) => {
          const status = row.original.agreement_status || "";
          const normalizedStatus = status.trim();
          const isEsignEnabled =
            normalizedStatus.toLowerCase() === "pending for amc approval";
          const hasViewedAgreement = Boolean(
            viewedAgreements[row.original.distributor_sequence_number]
          );

          return (
            <div className="flex items-center gap-2">
              <button
                onClick={() => handleDownload(row.original)}
                className="bg-[#F1F4F9] border border-[#E2E8F0] text-gray-800 px-3 py-1.5 rounded-xl transition-all flex items-center gap-1.5 hover:bg-gray-200 text-[11px] font-semibold"
                title="View Agreement"
              >
                <Eye size={14} />
                View Agreement
              </button>
              <button
                onClick={() => handleEsignInit(row.original)}
                disabled={!hasViewedAgreement || !isEsignEnabled}
                className={`bg-blue-50 border border-blue-200 text-blue-700 px-3 py-1.5 rounded-xl transition-all flex items-center gap-1.5 hover:bg-blue-100 disabled:opacity-30 disabled:cursor-not-allowed text-[11px] font-semibold`}
                title={
                  !hasViewedAgreement
                    ? "Please view agreement first"
                    : !isEsignEnabled
                    ? "Agreement status must be Pending for AMC Approval"
                    : "E-sign"
                }
              >
                <Zap size={14} />
                E-sign
              </button>
            </div>
          );
        },
      },
    ],
    [viewedAgreements]
  );

  return (
    <div className="w-full max-w-screen-xl mx-auto p-4 space-y-6">
      <ToastContainer position="top-right" autoClose={2000} hideProgressBar theme="colored" />

      {/* Header */}
      <div className="space-y-4">
        <h1 className="text-xl font-bold text-gray-800 tracking-tight">Agreement Verification & E-sign</h1>

        <div className="relative w-full">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-4 w-4 text-gray-400" />
          </div>
          <input
            type="text"
            placeholder="Search..."
            value={globalSearch}
            onChange={(e) => setGlobalSearch(e.target.value)}
            className="block w-full pl-10 pr-3 py-3 border border-gray-100 rounded-xl bg-white text-sm placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-gray-200 transition-all"
          />
        </div>
      </div>

      {/* Table Section */}
      {/* <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden"> */}
      <div className="p-1">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20 gap-4 text-gray-400">
            <Loader2 size={40} className="animate-spin text-gray-300" />
            <span className="text-sm">Loading records...</span>
          </div>
        ) : (
          <div className="overflow-x-auto custom-registry-table">
            <style>{`
                .custom-registry-table th {
                  color: #4f46e5;
                  font-size: 11px;
                  font-weight: 800;
                  text-transform: uppercase;
                  letter-spacing: 0.05em;
                  padding: 16px 12px;
                  border-bottom: 1px solid #f3f4f6;
                }
                .custom-registry-table td {
                  padding: 16px 12px;
                  vertical-align: middle;
                }
              `}</style>
            <ReusableTable
              columns={columns}
              data={data.filter(d =>
                (d.distributor_name || "").toLowerCase().includes(globalSearch.toLowerCase()) ||
                (d.arn_code || "").toLowerCase().includes(globalSearch.toLowerCase())
              )}
            />
          </div>
        )}
      </div>
      {/* </div> */}

      {/* Rejection Reason Modal */}
      {isRejectModalOpen && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black bg-opacity-60">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <h3 className="text-lg font-bold text-gray-800 mb-4">
              Rejection Reason
            </h3>
            <textarea
              className="w-full border rounded p-2 focus:outline-none focus:ring-2 focus:ring-red-500"
              rows="4"
              placeholder="Enter reason for rejection..."
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
            ></textarea>
            <div className="flex justify-end gap-3 mt-4">
              <Button
                variant="cancel"
                onClick={() => setIsRejectModalOpen(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={handleRejectSubmit}
                className="bg-red-600 text-white hover:bg-red-700"
              >
                Submit Rejection
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Approval Confirmation Modal */}
      {isApproveModalOpen && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black bg-opacity-60">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-sm p-6 flex flex-col items-center">
            <div className="rounded-full bg-green-100 p-3 mb-4">
              <svg
                className="w-8 h-8 text-green-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M5 13l4 4L19 7"
                ></path>
              </svg>
            </div>
            <h3 className="text-lg font-bold text-gray-800 mb-2">
              Confirm Approval
            </h3>
            <p className="text-gray-600 text-center mb-6">
              Are you sure you want to approve{" "}
              <b>{selectedDistributorForApproval?.distributor_name}</b>?
            </p>
            <div className="flex justify-center gap-3 w-full">
              <Button
                variant="cancel"
                onClick={() => setIsApproveModalOpen(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={confirmApprove}
                className="bg-green-600 text-white hover:bg-green-700 flex-1"
              >
                Confirm
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Ops E-sign Modal */}
      <CustomDialog
        visible={isEsignModalOpen}
        onHide={() => setIsEsignModalOpen(false)}
        header={`E-sign for ${selectedDistributorForEsign?.distributor_name}`}
        width="50vw"
      >
        <div className="space-y-4">
          <input
            type="text"
            value={esignName}
            onChange={(e) => setEsignName(e.target.value)}
            placeholder="In presence of"
            className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-purple-500"
          />

          <div className="flex justify-end gap-3">
            <Button
              variant="cancel"
              onClick={() => setIsEsignModalOpen(false)}
              disabled={esignLoading}
            >
              Cancel
            </Button>

            <Button
              variant="default"
              onClick={handleOpsEsignSubmit}
              disabled={esignLoading || !esignName.trim()}
            >
              {esignLoading ? "Submitting..." : "Submit E-sign"}
            </Button>
          </div>
        </div>
      </CustomDialog>
    </div>
  );
};

export default AMCListing;
